import { ArrowRight, BarChart3, Calendar, FileText } from "lucide-react"
import { FeatureCard, type FeatureCardType } from "src/components/empty-state/FeatureCard"
import { ScopeGate } from "src/components/ScopeGate"
import { Button } from "src/components/ui/button"

const features: FeatureCardType[] = [
  {
    icon: FileText,
    title: "Automated reports",
    description:
      "Generate comprehensive digests with trends, summaries, and actionable insights from your codebase and infrastructure data.",
    accentColor: "oklch(0.6 0.15 250)",
    bgGlow: "oklch(0.6 0.15 250 / 0.12)",
  },
  {
    icon: Calendar,
    title: "Flexible scheduling",
    description:
      "Set up daily, weekly, or custom schedules so your team always has the latest operational overview without manual effort.",
    accentColor: "oklch(0.75 0.15 80)",
    bgGlow: "oklch(0.75 0.15 80 / 0.12)",
  },
  {
    icon: BarChart3,
    title: "Trend analysis",
    description:
      "Surface patterns across alerts, deployments, and incidents so your team can proactively address hidden or recurring issues.",
    accentColor: "oklch(0.65 0.19 145)",
    bgGlow: "oklch(0.65 0.19 145 / 0.12)",
  },
]

interface DigestEmptyStateProps {
  onCreateClick: () => void
}

export default function DigestEmptyState({ onCreateClick }: DigestEmptyStateProps) {
  return (
    <div className="flex flex-col items-center">
      {/* Hero */}
      <section className="relative flex flex-col items-center px-6 pt-20 pb-16 overflow-hidden w-full">
        {/* Radial glow — dark mode only */}
        <div
          className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/3 w-[800px] h-[600px] rounded-full opacity-0 dark:opacity-30"
          style={{
            background: "radial-gradient(ellipse at center, oklch(0.75 0.15 80 / 0.5), transparent 70%)",
          }}
        />

        <div className="relative flex items-center gap-2 rounded-full border border-border bg-secondary/60 px-4 py-1.5 text-sm text-muted-foreground backdrop-blur-sm mb-8">
          <FileText className="h-3.5 w-3.5 text-primary" />
          <span className="font-mono text-xs tracking-wider uppercase">Digest Agent</span>
        </div>

        <h1 className="relative max-w-3xl text-center text-3xl font-bold tracking-tight text-foreground sm:text-4xl leading-[1.1]">
          Generate reports effortlessly
        </h1>

        <p className="relative mt-5 max-w-xl text-center text-sm leading-relaxed text-muted-foreground sm:text-base">
          Digest Agents create scheduled reports with trends, summaries, and actionable insights from your
          system. Stay informed without the manual overhead.
        </p>

        <ScopeGate scope="write:digests">
          <Button size="lg" className="relative mt-8 px-8 py-3 text-base" onClick={onCreateClick}>
            Create your first Digest Agent
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </ScopeGate>
      </section>

      {/* Feature cards */}
      <section className="px-6 pb-16 w-full">
        <div className="mx-auto grid max-w-5xl gap-4 md:grid-cols-3">
          {features.map((feature) => (
            <FeatureCard key={feature.title} feature={feature} />
          ))}
        </div>
      </section>
    </div>
  )
}
